import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface Product {
  id: string;
  name: string;
  slug: string;
  status: string;
  category: string;
  stats: { voteCount: number; ideaCount: number; feedbackCount: number; testerCount: number };
  updatedAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  concept: '#6366f1',
  voting: '#f59e0b',
  beta: '#3b82f6',
  shipped: '#10b981',
  archived: '#6b7280',
};

const STATUS_LABELS: Record<string, string> = {
  concept: '💡 Concept',
  voting: '🗳️ Voting',
  beta: '🧪 Beta',
  shipped: '🚀 Shipped',
  archived: '📦 Archived',
};

export function ProductsPage() {
  const { get } = useApi();
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const url = filter ? `/products?status=${filter}` : '/products';
    get(url)
      .then((data: any) => setProducts(data.products || []))
      .catch((e: unknown) => setError(String(e)));
  }, [filter]);

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;

  return (
    <div>
      <h1>🧴 Products</h1>

      {/* Status filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {['', 'concept', 'voting', 'beta', 'shipped', 'archived'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontWeight: filter === s ? 700 : 500, fontSize: '0.85rem',
              background: filter === s ? 'var(--color-primary, #18181b)' : 'var(--color-surface-2, #f3f4f6)',
              color: filter === s ? '#fff' : 'var(--color-text, #111)',
              transition: 'all 0.2s',
            }}
          >
            {s ? STATUS_LABELS[s] || s : 'All'}
          </button>
        ))}
      </div>

      {/* Product grid */}
      {products.length === 0 ? (
        <Card><p style={{ color: 'var(--color-text-secondary)' }}>No products found</p></Card>
      ) : (
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {products.map((p) => (
            <Card key={p.id}>
              <div onClick={() => navigate(`/products/${p.id}`)} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{p.name}</h3>
                <span style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                  background: `${STATUS_COLORS[p.status]}20`, color: STATUS_COLORS[p.status],
                }}>
                  {p.status.toUpperCase()}
                </span>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 0.75rem' }}>
                {p.category} • Updated {new Date(p.updatedAt).toLocaleDateString()}
              </p>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                <span>🗳️ {p.stats.voteCount}</span>
                <span>💡 {p.stats.ideaCount}</span>
                <span>📝 {p.stats.feedbackCount}</span>
                <span>🧪 {p.stats.testerCount}</span>
              </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
